import { registerActions } from "../actionRegistry";
/**
 * Partner and Dashboard management functions
 */

import {
  getDomElement,
  getDomElementOrThrow,
  setHtml,
  queryAll,
  idSuffix,
  fadeIn,
  fadeOut,
} from "../utils/dom";
import { config } from "../config";

/**
 * Every `.dashboard-compact` row paired with its raw partner/artist id. The row
 * element IS `#partnercompact-<id>`, so the legacy `$("#partnercompact-" + raw_id)`
 * lookups just resolved back to the row already in hand.
 */
function compactRows(): { el: HTMLElement; id: string }[] {
  return Array.from(queryAll<HTMLElement>(".dashboard-compact"), (el) => {
    if (!el.id) throw new Error("Expected element to have an id attribute");
    return { el, id: idSuffix(el, "partnercompact-") };
  });
}

/** Reset every row to visible (the "table-row" default these rows are laid out with). */
function showAllCompactRows(): void {
  for (const { el } of compactRows()) el.style.display = "table-row";
}

/** Toggle the `#toggle-selectall` / `#toggle-<id>` checkbox icon. */
function setToggleIcon(toggleId: string, checked: boolean): void {
  const icon = getDomElement(toggleId);
  if (!icon) return;
  if (checked) {
    icon.classList.remove("far", "fa-square");
    icon.classList.add("fas", "fa-check-square");
  } else {
    icon.classList.remove("fas", "fa-check-square");
    icon.classList.add("far", "fa-square");
  }
}

/**
 * Flip one row's selection, and nothing else.
 *
 * ⚠️ Split out of `togglePartner` because the summary rebuild it used to drag along made
 * every BULK action quadratic. `togglePartnerParse` rescans every row on the tab and rebuilds
 * the floater's HTML; `togglePartner` called it once per row; and `partnerselect` /
 * `partnerSelectAll` call the toggle once per row. On the artists tab that is 1,307 rows, so
 * one press of "Select artists with uploads" did on the order of a thousand full rescans —
 * well over a million element visits — plus a thousand `setHtml` rebuilds and a thousand
 * fade animations. The tab locked up for seconds.
 *
 * Every bulk caller now loops over THIS and calls `togglePartnerParse()` once at the end.
 * The semantics are unchanged: it still toggles rather than setting, so a caller that relies
 * on flipping an already-selected row behaves exactly as it did.
 */
function togglePartnerState(id: string): void {
  const stateEl = getDomElement<HTMLInputElement>("state-" + id);
  if (!stateEl) return;
  const selected = stateEl.value == "0";
  stateEl.value = selected ? "1" : "0";
  setToggleIcon("toggle-" + id, selected);
  getDomElement("partnercompact-" + id)?.classList.toggle("dashboard-compact-selected", selected);
}

/**
 * Toggle partner selection — the single-row path, dispatched from a row click
 * (`callAttrs("togglePartner", id)` in `dashboard/tables.tsx`).
 *
 * One row changed means the summary is one rescan behind, so it pays for it immediately.
 */
export function togglePartner(id: string): void {
  togglePartnerState(id);
  togglePartnerParse();
}

/**
 * Parse selected partners and update UI
 */
export function togglePartnerParse(): void {
  const selectedIds = compactRows()
    .filter(({ id }) => getDomElement<HTMLInputElement>("state-" + id)?.value == "1")
    .map(({ id }) => id);
  const selected = selectedIds.length;
  const selected_str = selectedIds.join(",");

  if (selected > 0) {
    const partner_s = selected == 1 ? "" : "s";
    // The spans carry data-action="modal" so actionDelegator dispatches them —
    // the old code hand-bound a click handler per span because the bare
    // data-modal attribute alone is not something the delegator listens for.
    const isArtists = getDomElement("dashboard-artists") != null;
    const noun = isArtists ? "artist" : "partner";
    const newsletterModal = isArtists ? "sendnewsletter-artist" : "sendnewsletter";
    const groupsModal = isArtists ? "addtoartistgroups" : "addtopartnergroups";
    const groupsLabel = isArtists ? "Add to Artist Groups" : "Add to Partner Groups";
    setHtml(
      "dashboard-floater",
      `<div class="dashboard-floater-header">${selected} ${noun}${partner_s} selected</div>` +
        `<div class="dashboard-floater-subheader">Perform multi actions on selected ${noun}s</div>` +
        `<div class="dashboard-floater-buttons"><span class="button medium green" data-action="modal" data-modal="${newsletterModal}" data-size="large"><i class="fa fa-envelope"></i> Send newsletter</span></div>` +
        `<div class="dashboard-floater-buttons"><span class="button medium yellow" data-action="modal" data-modal="${groupsModal}" data-size="dynamic"><i class="fas fa-users"></i> ${groupsLabel}</span></div>`
    );
    fadeIn("dashboard-floater");
  } else {
    fadeOut("dashboard-floater");
  }
  const selectedPartnersEl = getDomElement<HTMLInputElement>("selectedpartners");
  if (selectedPartnersEl) {
    selectedPartnersEl.value = selected_str;
  }
}

/**
 * Select all partners
 */
export function partnerSelectAll(): void {
  const selectAllEl = getDomElementOrThrow<HTMLInputElement>("state-selectall");
  if (selectAllEl.value == "0") {
    selectAllEl.value = "1";
    setToggleIcon("toggle-selectall", true);
    for (const { el, id } of compactRows()) {
      const stateEl = getDomElement<HTMLInputElement>("state-" + id);
      if (stateEl?.value == "0" && el.style.display != "none") {
        togglePartnerState(id);
      }
    }
  } else {
    selectAllEl.value = "0";
    setToggleIcon("toggle-selectall", false);
    for (const { id } of compactRows()) {
      if (getDomElement<HTMLInputElement>("state-" + id)?.value == "1") {
        togglePartnerState(id);
      }
    }
  }
  // Once, for the whole action — see the note on `togglePartnerState`.
  togglePartnerParse();
}

/**
 * Select partners by type
 */
function eachPartner(predicate: (raw_id: string) => boolean): void {
  for (const { el, id } of compactRows()) {
    if (el.style.display != "none" && predicate(id)) {
      togglePartnerState(id);
    }
  }
}

export function partnerselect(what: string): void {
  const selectAllEl = getDomElement<HTMLInputElement>("state-selectall");
  if (selectAllEl) {
    selectAllEl.value = "0";
  }
  setToggleIcon("toggle-selectall", false);

  // Deselect all first
  for (const { id } of compactRows()) {
    if (getDomElement<HTMLInputElement>("state-" + id)?.value == "1") {
      togglePartnerState(id);
    }
  }

  if (what == "invited") {
    eachPartner((id) => getDomElement("invited-" + id) != null);
  }

  if (what == "inviteddl") {
    eachPartner((id) => {
      if (getDomElement("invited-" + id) == null) return false;
      const el = getDomElement("downloaded-" + id);
      return !!el && parseInt(el.innerHTML) > 0;
    });
  }

  if (what == "invitednodl") {
    eachPartner((id) => {
      if (getDomElement("invited-" + id) == null) return false;
      const el = getDomElement("downloaded-" + id);
      return !!el && parseInt(el.innerHTML) == 0;
    });
  }

  if (what == "invitedfewdl") {
    eachPartner((id) => {
      if (getDomElement("invited-" + id) == null) return false;
      const el = getDomElement("downloaded-" + id);
      return !!el && parseInt(el.innerHTML) < 50;
    });
  }

  if (what == "artistnoul") {
    eachPartner((id) => {
      const el = getDomElement("uploaded-" + id);
      return !!el && parseInt(el.innerHTML) == 0 && getDomElement("terminated-" + id) == null;
    });
  }

  if (what == "artistul") {
    eachPartner((id) => {
      const el = getDomElement("uploaded-" + id);
      return !!el && parseInt(el.innerHTML) > 0 && getDomElement("terminated-" + id) == null;
    });
  }

  // Once, after the deselect pass AND whichever select pass ran above. Putting it inside
  // `eachPartner` would charge the rebuild twice for every branch.
  togglePartnerParse();
}

/**
 * Filter by group — shared body of onlygroup()/onlygroupartist(), which differ
 * only in which "only mine" checkbox they clear and disable.
 *
 * One pass, writing a row's `display` only when it actually changes.
 *
 * It used to make three: hide the not-mine rows, then show EVERY row, then hide the
 * non-members — rebuilding the row list from a fresh `querySelectorAll` each time and
 * assigning `display` to all 1,307 rows on the artists tab in between. The first pass was
 * already dead (a previous reader noted its writes were "immediately undone by
 * showAllCompactRows", which they were), and the show-everything pass existed only to undo
 * the last filter. Computing the target per row and writing on difference reaches the same
 * final state, which is the only state anybody saw.
 *
 * ⚠️ "Is a group selected?" is a string test, not `parseInt(String(id)) > 0`.
 *
 * That is what it used to be, and it read like an integer-id check while being nothing of
 * the kind: group ids are uuids, and it only worked because `parseInt` keeps the leading hex
 * digits of one. Every group in the database today is a uuidv7 beginning `06a9…`, so it
 * yielded 6 and the filter ran. A uuid whose first character is `a`–`f` parses to NaN, and
 * the filter would then have silently done nothing at all — no error, no empty state, just a
 * dropdown that does not filter.
 *
 * The two values that mean "no group" are kept exactly: `""` (the "-- Artist groups --"
 * option) and `"0"`, which `parseInt` also rejected. The marker lookup uses the original
 * `id`, never a parsed number.
 */
function filterByGroup(id: string | number, onlyMineId: string): void {
  const onlyMineEl = getDomElement<HTMLInputElement>(onlyMineId);
  if (onlyMineEl?.checked) onlyMineEl.checked = false;
  onlyMineEl?.setAttribute("readonly", "readonly");
  onlyMineEl?.setAttribute("disabled", "disabled");

  const chosen = String(id);
  const filtering = chosen !== "" && chosen !== "0";
  if (!filtering) {
    onlyMineEl?.removeAttribute("disabled");
    onlyMineEl?.removeAttribute("readonly");
  }

  for (const { el, id: raw_id } of compactRows()) {
    const want =
      !filtering || getDomElement("group-" + chosen + "-" + raw_id) != null ? "table-row" : "none";
    if (el.style.display !== want) el.style.display = want;
  }
}

export function onlygroup(id: string | number): void {
  filterByGroup(id, "onlymine");
}

/**
 * Filter by artist group
 */
export function onlygroupartist(id: string | number): void {
  filterByGroup(id, "onlymineartist");
}

/**
 * Filter to show only mine
 */
export function onlymine(): void {
  if (getDomElement<HTMLInputElement>("onlymine")?.checked) {
    hideRowsWhereNotMine("owner-");
  } else {
    showAllCompactRows();
  }
}

/** Hide every row whose `<prefix><id>` input doesn't hold the current user's id. */
function hideRowsWhereNotMine(prefix: string): void {
  for (const { el, id } of compactRows()) {
    const ownerEl = getDomElement<HTMLInputElement>(prefix + id);
    if (ownerEl && ownerEl.value != String(config.authUser)) el.style.display = "none";
  }
}

/**
 * Filter to show only my artists
 */
export function onlymineartist(): void {
  showAllCompactRows();

  const require_bio = getDomElement<HTMLInputElement>("onlybio")?.checked === true;
  const mine = getDomElement<HTMLInputElement>("onlymineartist")?.checked === true;
  const signed = getDomElement<HTMLInputElement>("onlymineartistsigned")?.checked === true;

  if (mine && !signed) {
    hideRowsWhereNotMine("owner-");
  }
  if (signed && !mine) {
    hideRowsWhereNotMine("signedby-");
  }
  if (signed && mine) {
    // Both checked: a row survives only if BOTH signedby and owner are me.
    for (const { el, id } of compactRows()) {
      const signedByEl = getDomElement<HTMLInputElement>("signedby-" + id);
      const ownerEl = getDomElement<HTMLInputElement>("owner-" + id);
      if (
        (signedByEl && signedByEl.value != String(config.authUser)) ||
        (ownerEl && ownerEl.value != String(config.authUser))
      ) {
        el.style.display = "none";
      }
    }
  }

  if (require_bio) {
    for (const { el, id } of compactRows()) {
      if (getDomElement("hasbio-" + id) == null) el.style.display = "none";
    }
  }
}

registerActions({
  togglePartner,
  togglePartnerParse,
  partnerSelectAll,
  partnerselect,
  onlygroup,
  onlygroupartist,
  onlymine,
  onlymineartist,
});
